import { Fragment, ReactNode, useEffect, useRef, useState, useMemo } from 'react';
import { useParams } from 'react-router';
import { IPage } from '../Components/Page';
import { eventStore, IStoreEvent, IStoreGate } from '../Services/EventsStore';
import { execAsync, formatDate, formatValidationMessage, isHoneywellDevice, useInitAsync, validateColor } from '../Utils';
import Home from './Home';
import Button from '@mui/material/Button';
import { CognexScanner } from "../Scanner/CognexScanner"
import session from '../Services/Session';
import { beepFail, beepOk, playSoundAsync, vibrate } from '../Services/SoundPlayer';
import { ZXingScanner } from '../Scanner/ZXingScanner';
import { delayAsync, getBackCameraIdAsync } from '../Scanner/Utils';
import { isCameraScanner } from '../Scanner/Abstraction/ICameraScanner';
import { IVerifyTicketResponse } from '../Services/ApiClient';
import { useOperation } from '../Hooks/OperationContext';
import { IScanner, IScannerCode } from '../Scanner/Abstraction/IScanner';
import { userSettingsStore } from '../Services/UserSettingsStore';
import { HoneywellScanner } from '../Scanner/HoneywellScanner';
import { appSettings } from '../AppSettings';
import SuccessIcon from "../Images/ok.svg";
import FailIcon from "../Images/ko.svg";
import Title from '../Components/Title';
import WarningIcon from "../Images/warning.svg";
import { ZBarScanner } from '../Scanner/ZBarScanner';
import { useActions } from '../Hooks/Actions';
import { useNetwork, useOnlineCheck } from '../Hooks/Network';
import "./Scanner.scss"
import useAppBar from '../Hooks/AppBarContext';
import Settings from './Settings';
import TicketSearch from './TicketSearch';
import { Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportIcon from '@mui/icons-material/Report';
import { NativeScanner } from '../Scanner/NativeScanner';
import { isNativeDevice } from '../Platform';
import OfflineScans from './OfflineScans';
import { ticketValidator } from '../Services/TicketValidator';
import { KeyboardScanner } from '../Scanner/KeyboardScanner';
import Login from './Login';
import EventList from './EventList';
import { Monitor } from '@mui/icons-material';
import EventMonitor from './EventMonitor';
import { useEventStats } from '../Hooks/EventStatsContext';

interface IScannerProps {
    event?: IStoreEvent;
    gate?: IStoreGate;
}

interface ILastScan {
    result: IVerifyTicketResponse[];
    time: Date;
}

interface IScanResultProps {
    result: IVerifyTicketResponse[];
    gate: string;
    title: string;
    asyncMode: boolean;
    info?: string;
    onScan: () => void;
}

let scanner: IScanner;

let onVisibilityChanged: () => void;


function generateMessage(result: IVerifyTicketResponse[]) {

    const isSuccess = result.length > 0 && result.every(a => a.type == "OK");
    const isFail = result.length == 0 || result.every(a => a.type != "OK");
    const isPass = result.length > 1;

    let msg: string;

    if (!isSuccess && !isFail) {
        const failCount = result.filter(a => a.type != "OK").length;
        if (failCount == 1)
            msg = `${failCount} biglietto su ${result.length} è invalido.`;
        else
            msg = `${failCount} biglietti su ${result.length} sono invalidi.`;
    }
    else {
        if (!isPass) {
            msg = formatValidationMessage(result[0].msg, "long") + "\n" + result[0].msg_data;
        }
        else {
            if (isSuccess)
                msg = `${result.length} biglietti validi`;
            else
                msg = `${result.length} sono invalidi`;
        }
    }

    return msg;
}

function ScanResult(props: IScanResultProps) {

    const detailsAnchor = useRef();

    const [showDetails, setShowDetails] = useState(false);

    const isSuccess = props.result.length > 0 && props.result.every(a => a.type == "OK");
    const isFail = props.result.length == 0 || props.result.every(a => a.type != "OK");
    const isPass = props.result.length > 1;
    const isOffline = props.result.some(a => a.isOffline);

    let msg: ReactNode;
    let iconSrc: string;
    let className: string;
    let textLeft: string;
    let textRight: string;
    let bkColor: string;

    if (!isSuccess && !isFail) {
        const failCount = props.result.filter(a => a.type != "OK").length;
        if (failCount == 1)
            msg = `${failCount} biglietto su ${props.result.length} è invalido.`;
        else
            msg = `${failCount} biglietti su ${props.result.length} sono invalidi.`;
        iconSrc = WarningIcon;
        className = "warning";
    }
    else {
        if (!isPass) {
            msg = (<>
                <p>{formatValidationMessage(props.result[0].msg, "long")}</p>
                {props.result[0].msg_data ? <p>{props.result[0].msg_data}</p> : null}
            </>);
        }
        else {
            if (isSuccess)
                msg = <><span className="tickets-num">{props.result.length}</span> biglietti validi</>;
            else
                msg = <><span className="tickets-num">{props.result.length}</span> biglietti invalidi</>;
        }

        if (isSuccess) {
            iconSrc = SuccessIcon;
            className = "success";
            textLeft = "OK"
            textRight = "Biglietto Valido"
        }
        else {
            iconSrc = FailIcon;
            className = "fail";
        }
    }

    if (props.result.length > 0)
        bkColor = validateColor(props.result[0].color);

    return (
        <div className="scan-result" ref={detailsAnchor}>

            <header>
                <Title>{props.title}</Title>
                <div className="gate">
                    <label>Gate</label>
                    <span>{props.gate}</span>
                </div>
            </header>

            <div className="result-icon">
                <span>{textLeft}</span>
                <img src={iconSrc} />
                <span>{textRight}</span>
            </div>
            <div style={{backgroundColor: bkColor}}  className={"message " + className + (isOffline ? " offline" : "")}>
                {msg}
                {isOffline && <span className="offline-info">Verifica con lista offline</span> }
                {props.result.length > 1 && <Button onClick={()=> setShowDetails(true)}>Dettagli</Button> }
            </div>

            {!props.asyncMode && <Button size="large" variant="contained" className="scan" onClick={props.onScan}>Next Scan</Button>}
            {props.asyncMode && <>
                <div className="scan-msg">Inquadrare un codice</div>
                <div className="info">{props.info}</div> 
            </>}
            <Dialog
                className="ticket-details"
                open={showDetails}
                onClose={() => setShowDetails(false)}
            >
                <DialogTitle>Dettagli Titoli</DialogTitle>
                <DialogContent>
                    <List>
                        {props.result.map((a, i) => <ListItem key={i} className={a.type == "OK" ? "success" : "fail"}>
                            <ListItemIcon>
                                {a.type == "OK" ? <CheckCircleIcon /> : <ReportIcon />}
                            </ListItemIcon>
                            <ListItemText primary={a.msg_data} secondary={a.msg} />
                        </ListItem>)}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDetails(false)}>Chiudi</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export function ScannerContent(props: IScannerProps) {

    const operation = useOperation();

    const actions = useActions();

    const network = useNetwork();

    const setAppBar = useAppBar();

    const eventStats = useEventStats();

    const [event, setEvent] = useState(props.event);

    const [gate, setGate] = useState(props.gate);

    const [isScanning, setIsScanning] = useState(false);

    const [lastScan, setLastScan] = useState<ILastScan>();

    const { eventId, gateId } = useParams();

    const settings = userSettingsStore.current;

    if (!scanner) {
        if (settings.enableScannerBLT) 
            scanner = new KeyboardScanner(); 
        else if ("scanner" in window)
            scanner = new NativeScanner();
        else if (isHoneywellDevice())
            scanner = new HoneywellScanner(appSettings.honeywellLib);
        else {
            switch (appSettings.cameraEngine) {
                case "Cognex":
                    scanner = new CognexScanner(appSettings.cognexLib);
                    break;
                case "ZBar":
                    scanner = new ZBarScanner({ useCanvas: appSettings.useCanvas });
                    break;
                default:
                    scanner = new ZXingScanner({ useCanvas: appSettings.useCanvas });
                    break;
            }
        }
    }

    const onlineCheck = useOnlineCheck();

    const lastScanText = useMemo(
        () => lastScan ? "Ultima scansione " + formatDate(lastScan.time, true) : "",
    [lastScan]);

    const isAsyncScanner = useMemo(() => {
        try {
            return scanner instanceof NativeScanner && window.scanner.scannerType() == 1;
        }
        catch {
            return false;
        }
    }, [scanner]);


    useInitAsync(() => network(async () => {

        if (!event && eventId) {
            session.activeEvent = await eventStore.getEventByIdAsync(eventId);
            setEvent(session.activeEvent);
        }

        if (!gate && gateId) {
            session.activeGate = await eventStore.getEventGateAsync(eventId, gateId);
            setGate(session.activeGate);
        }

        await onlineCheck();

        if (isAsyncScanner)
            await scanner.openAsync();

        await eventStats.updateAsync();

    }));

    useEffect(() => {
        return () => {

            if (scanner?.onTrigger)
                scanner.onTrigger(null);

            if (scanner?.onNewCode)
                scanner.onNewCode(null);

            if (isAsyncScanner)
                scanner.closeAsync();
        }
    }, []);

    useEffect(() => {
        if (!onVisibilityChanged) {
            onVisibilityChanged = () => {
                console.log(document.visibilityState);
                if (document.visibilityState === "hidden" && scanner?.isScanning && preview)
                    scanner.stopScanAsync();
            };
            document.addEventListener("visibilitychange", onVisibilityChanged);
        }

    }, []);

    useEffect(() => () => {

        if (onVisibilityChanged) {
            document.removeEventListener("visibilitychange", onVisibilityChanged);
            onVisibilityChanged = null;
        }

        scanner.closeAsync();
    }, []);


    useEffect(() => {

        setAppBar({
            title: "Access Control",
            onUserLogoClick: () => {

                const logo = userSettingsStore.current?.logo;
                if (!logo || logo.indexOf("logo_boxol") != -1) 
                    actions.ticketSearch(event?.id, gate?.code);
                
            },
            actions: [
                {
                    body: Settings.title,
                    icon: Settings.icon,
                    executeAsync: async () => actions.settings()
                },
                {
                    body: EventList.title,
                    icon: EventList.icon,
                    executeAsync: async () => actions.eventList()
                },
                {
                    body: TicketSearch.title,
                    icon: TicketSearch.icon,
                    executeAsync: async () => actions.ticketSearch(event?.id, gate?.code)
                },
                {
                    body: EventMonitor.title,
                    icon: EventMonitor.icon,
                    executeAsync: async () => actions.openMonitorAsync(event?.id)
                },
                {
                    body: OfflineScans.title,
                    icon: OfflineScans.icon,
                    executeAsync: async () => actions.offlineScans(event?.id)
                },
                {
                    body: "Cambia PIN",
                    icon: Login.icon,
                    executeAsync: async () => actions.login()
                }
            ]
        });

    }, [event?.id, gate?.code]);
 

    const preview = scanner?.createPreview ? scanner.createPreview(lastScanText, isScanning, lastScan?.time) : null;


    const onNewCodeAsync = async (result: IScannerCode) => {

        console.log(result);

        if (isCameraScanner(scanner)) {
            settings.scanner.flashOn = scanner.isFlashOn;
            settings.scanner.cameraIndex = scanner.activeCamera;
            await userSettingsStore.saveAsync();
        }

        if (!result?.code)
            return;

        vibrate(true);

        onlineCheck();

        let verResult = await network(() => ticketValidator.verifyAsync({
            eventID: event.id,
            barcode: result.code,
            ref_gate: gate?.code ?? "0",
            direction: 'E',
            ref_device: session.deviceId
        }), { showBigError: true, disableRetry: !appSettings.showRetryOnScanError });

        let allOk = verResult.length > 0 && verResult.every(a => a.type == "OK");

        
        if (verResult.length > 0) {
            setLastScan({
                result: verResult,
                time: new Date()
            });

            setTimeout(async () => {
                if (allOk) {
                    for (const item in verResult) {
                        await playSoundAsync(beepOk);
                        await delayAsync(300);
                    }

                }
                else {
                    await playSoundAsync(beepFail);
                    await delayAsync(50);
                    await playSoundAsync(beepFail);
                }

            }, 0);

        }

        if (allOk && settings.continusMode && scanner instanceof NativeScanner && window.scanner.useNativeInterface()) {
            const bkColor = validateColor(verResult[0].color);
            window.scanner.showMessage(generateMessage(verResult), "success", bkColor, null);
        }

            
        return allOk;
    }

    const scan = () => {

        try {
            if (isNativeDevice())
                nativeDevice.checkLicence()
        }
        catch {
            console.warn("Errore durante la verifica della licenza")   
        }

        if (!(scanner instanceof KeyboardScanner))
            setLastScan(null);

        onlineCheck();

        operation.begin({ showProgress: false });

        execAsync(async () => {

            try {
                await scanner.openAsync();

                if (isCameraScanner(scanner)) {

                    if (settings.scanner?.cameraIndex === undefined) {

                        const backCamera = await getBackCameraIdAsync();

                        console.log("Back camera id " + backCamera);

                        const index = scanner.cameras.findIndex(a => a.deviceId === backCamera);

                        if (index != -1)
                            await scanner.setCameraAsync(index);
                        else
                            console.log("Back camera not found with deviceId");
                    }
                    else
                        await scanner.setCameraAsync(settings.scanner.cameraIndex);

                    if (settings.scanner?.flashOn !== undefined)
                        await scanner.updateFlashAsync(settings.scanner.flashOn);
                }
            }
            catch (ex) {
                console.warn(ex);
                operation.showAlert("Error durante l'apertura dello scanner", "error", 3000);
                return;
            }
            finally {
                operation.end();
            }

            setIsScanning(true);

            if (!preview && settings.continusMode && scanner.startMultiScanAsync) {
                await scanner.startMultiScanAsync(onNewCodeAsync);
                setIsScanning(false);
            }
            else {

                let result: IScannerCode;

                const mockCode = localStorage.getItem("MockCode");
                if (appSettings.debugMode && mockCode?.length > 0) {
                    result = {
                        code: mockCode,
                        type: ""
                    };
                }
                else
                    result = await scanner.scanSingleAsync();

                const allOk = await onNewCodeAsync(result);

                setIsScanning(false);

                if (allOk && settings.continusMode) {
                    await delayAsync(settings.scanDelay !== undefined ? settings.scanDelay : appSettings.scanDelay);
                    scan();
                }
            }

        });         
    }

    const onGateClick = () => {
        actions.selectGate(eventId);
    }

    if (scanner.onTrigger) { 
        scanner.onTrigger(isOn => { 
            if (isOn)
                scan();
            else {
                if (scanner.isScanning)
                    scanner.stopScanAsync();
            }
        });
    }

    if (isAsyncScanner && scanner.onNewCode) {
        scanner.onNewCode(code => {
            onNewCodeAsync(code);
        });
    }

    if (!event || !gate)
        return null;


    return (
        <Fragment>{lastScan ? 
            <ScanResult
                gate= {gate.name}
                result = {lastScan.result}
                onScan={scan}
                info={lastScanText}
                asyncMode={isAsyncScanner }
                title = { event.title }
            /> :
            <Fragment>
                <header><Title>{event.title}</Title></header>
           
                <div className="gate" onClick={onGateClick}>
                    <label>Gate</label>
                    <span>{gate.name}</span>
                </div>
                {!isAsyncScanner && <Button size="large" variant="contained" className="scan" onClick={scan}>Scansiona</Button>}

                {isAsyncScanner && <>
                    <div className="scan-msg">Inquadrare un codice</div>
                    <div className="info">{lastScanText}</div> 
                </>}
            </Fragment>}

            {preview}
        </Fragment>
    );
}

const Scanner : IPage = {

    name: "scanner",
    title: "Scanner",
    route: "/scan/:eventId/:gateId",
    container: Home,
    content: <ScannerContent />
}

export default Scanner;